/**
 * =========================================================================================
 * Activity Timeline Page
 * =========================================================================================
 *
 * Shows subscription activity history using socket events:
 * - getAllActivities: Fetches all activities across subscriptions
 * - getActivityTypes: Fetches activity type definitions (cached)
 *
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';
import UCustomScrollBar from 'ubs-framework/v2/UCustomScrollBar';
import UIcon from 'ubs-framework/v2/UIcon';
import { showToastError } from 'ubs-framework/v2/common/function';
import ActivityFilter from './components/activity-filter';
import ActivityTimeline from './components/activity-timeline';
import { sendData } from '../../../../store/actions';
import useSocket from '../../../common/useSocket';
import AppHeader from '../../../common/components/setting-app-header';

const ACTIVITY_STATS_CONFIG = [
    { key: 'totalTransactions', label: 'Total Activities', icon: Icon.Clock, colorCode: COLOR?.u_icon_bg },
    { key: 'totalUserEvents', label: 'User Actions', icon: Icon.User, colorCode: COLOR?.u_blue },
    { key: 'totalSystemEvents', label: 'System Events', icon: Icon.Setting, colorCode: COLOR?.u_font },
    { key: 'paymentActivitys', label: 'Payments', icon: Icon.CreditCard, colorCode: COLOR?.u_green },
    { key: 'changesActivitys', label: 'Changes', icon: Icon.Reload, colorCode: COLOR?.u_orange },
];

const BillingActivity = () => {
    const dispatch = useDispatch();
    const _FILTER = useSelector(e => e?.filter?.billingActivity);

    const [activities, setActivities] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [activityTypes, setActivityTypes] = useState([]);
    const [stats, setStats] = useState(null);

    const typesLoadedRef = useRef(false);
    const prevCategoryRef = useRef(_FILTER?.category);
    const pendingSkipRef = useRef(0);
    const isLoadMorePendingRef = useRef(false);

    const getData = useCallback((isLoadMore = false) => {
        if (isLoadMore) {
            setLoadingMore(true);
            isLoadMorePendingRef.current = true;
        } else {
            setDataLoading(true);
            isLoadMorePendingRef.current = false;
        }

        const skip = isLoadMore ? activities.length : 0;
        pendingSkipRef.current = skip;

        const payload = {
            limit: _FILTER?.limit || 20,
            skip,
            ...(_FILTER?.category && { category: _FILTER.category }),
        };

        sendData(dispatch, 'billing|cashfree|subscription|getAllActivities', payload);
    }, [_FILTER?.category, _FILTER?.limit, activities.length]);

    const getTypes = useCallback(() => {
        if (!typesLoadedRef.current) {
            sendData(dispatch, 'billing|cashfree|subscription|getActivityTypes', {});
        }
    }, []);

    const { off, refresh } = useSocket(res => {
        if (!res.error) {
            switch (res.event) {
                case 'billing|cashfree|subscription|getAllActivities': {
                    const newActivities = res?.data?.activities || [];
                    const pagination = res?.data?.pagination || {};
                    const responseSkip = Number(pagination?.skip ?? 0);
                    const isLoadMoreResponse = responseSkip > 0;

                    // Ignore stale load-more responses after tab change
                    if (isLoadMoreResponse && !isLoadMorePendingRef.current) {
                        setLoadingMore(false);
                        break;
                    }

                    if (isLoadMoreResponse) {
                        setActivities(prev => [...prev, ...newActivities]);
                    } else {
                        setActivities(newActivities);
                    }

                    isLoadMorePendingRef.current = false;
                    setLoadingMore(false);
                    setDataLoading(false);

                    setTotalRecords(pagination?.total || 0);
                    setHasMore(pagination?.hasMore || false);

                    // Calculate stats from the first load
                    if (!isLoadMoreResponse && newActivities.length > 0) {
                        setStats(res?.data?.summary || {});
                    }
                    break;
                }
                case 'billing|cashfree|subscription|getActivityTypes': {
                    setActivityTypes(res?.data?.types || []);
                    typesLoadedRef.current = true;
                    break;
                }
                default:
                    return true;
            }
        } else {
            setDataLoading(false);
            setLoadingMore(false);
            showToastError(res?.data?.error || 'Failed to load activities. Please try again.');
        }
    });

    useEffect(() => {
        refresh();
        getTypes();
        return () => off();
    }, []);

    useEffect(() => {
        refresh();
        // Reset all data when category changes
        if (prevCategoryRef.current !== _FILTER?.category) {
            setActivities([]);
            setTotalRecords(0);
            setHasMore(false);
            isLoadMorePendingRef.current = false;
            prevCategoryRef.current = _FILTER?.category;
        }
        getData(false);
    }, [_FILTER?.category]);

    const handleLoadMore = () => {
        getData(true);
    };

    return (
        <React.Fragment>
            <div className="u_layout-list">
                <AppHeader title="Activity Timeline">
                    <ActivityFilter />
                </AppHeader>

                <div className="u_layout-scrollview u_flex u_direction-column u_overflow-hidden">
                    {/* Stats bar */}
                    {stats && !_FILTER?.category && (
                        <div className="u_subsciption-header u_px-16 u_pt-16 u_flex-shrink u_bb-light">
                            <div className="u_row">
                                {ACTIVITY_STATS_CONFIG.map(item => {
                                    const value = stats[item.key] || 0;
                                    return (
                                        <div key={item.key} className="u_col-2 u_mb-16">
                                            <div className="u_bg-white u_b-light u_radius-8 u_px-16 u_py-16">
                                                <div className="u_flex-align">
                                                    <UIcon
                                                        color={item.colorCode}
                                                        thumb={48}
                                                        icon={<item.icon size={20} fill={item.colorCode} />}
                                                        type="square"
                                                    />
                                                    <div className="u_pl-12">
                                                        <h5 className="u_semi-large-font u_fw-700 u_color-title">{value}</h5>
                                                        <p className="u_small-font u_color-font u_mt-4">{item.label}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Timeline */}
                    <div className="u_flex-1 u_overflow-hidden">
                        <UCustomScrollBar>
                            <ActivityTimeline
                                activities={activities}
                                totalRecords={totalRecords}
                                dataLoading={dataLoading}
                                hasMore={hasMore}
                                onLoadMore={handleLoadMore}
                                loadingMore={loadingMore}
                            />
                        </UCustomScrollBar>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default BillingActivity;
